// Copyright (c) Docugami, Inc. All rights reserved.
import { useState } from 'react';
import { Dropdown, DialogType, DialogContent, IDropdownOption, Dialog, DialogFooter, PrimaryButton, DefaultButton } from "@fluentui/react";
import { AppState } from '../store';
import { useSelector } from 'react-redux';
import { primaryTheme } from '../components/shell/presentation/AppStyles';
import { setLocalStorageOrganizationId } from 'utils/localStorageHelpers';
import { Telemetry } from 'telemetry/TelemetryManager';
import { OrgSwitcherChangingOrgSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';

export interface IOrgSwitcher {
    switchOrgDisabled: boolean;
    switchOrg: () => void;
    getOrgSwitcherDialog: () => JSX.Element;
}

export function useOrgSwitcher(): IOrgSwitcher {
    const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
    const organizations = useSelector((state: AppState) => state.organizations);
    let currentOrg = {
        key: organizations.currentOrganization?.id,
        text: organizations.currentOrganization?.name
    };
    const [chosenOption, setChosenOption] = useState<IDropdownOption>(currentOrg);

    const switchOrgDisabled = organizations.organizationList.allIds.length < 2;

    function switchOrg(): void {
        setChosenOption(currentOrg);
        setShowOrgSwitcher(true);
    }

    function orgSwitcherOnChange(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number): void {
        if (option) {
            setChosenOption(option);
        }
    }

    function cancelChangeOrg(): void {
        setChosenOption({ key: '', text: '' });
        setShowOrgSwitcher(false);
    }

    function commitChooseOrg(): void {
        setShowOrgSwitcher(false);
        Telemetry.trackEvent<OrgSwitcherChangingOrgSchema>({
            previousOrgId: currentOrg.key,
            nextOrgId: chosenOption.key,
            eventName: TelemetryEvent.OrgSwitcherChangingOrg
        });
        setLocalStorageOrganizationId(chosenOption?.key as string);
        window.location.href = `${window.location.origin}/org/switch/#oid=${chosenOption?.key}`
    }

    function getOrgOptions(): IDropdownOption[] {
        let items: IDropdownOption[] = [];

        for (let id of organizations.organizationList.allIds) {
            items.push({
                key: id,
                text: organizations.organizationList.indexed[id].name
            });
        }

        return items.sort((a, b) => a.text.localeCompare(b.text));
    }

    function getOrgSwitcherDialog(): JSX.Element {
        const cancelButtonStyle = {
            border: 'none',
            color: primaryTheme.palette.neutralTertiary
        }

        return (
            <Dialog
                hidden={!showOrgSwitcher}
                onDismiss={cancelChangeOrg}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: "Switch Workspace"
                }}
                modalProps={{
                    isBlocking: true,
                    styles: { main: { maxWidth: 450 } }
                }}>
                <DialogContent>
                    <Dropdown
                        label="Organizations"
                        selectedKey={chosenOption.key as string}
                        options={getOrgOptions()}
                        onChange={orgSwitcherOnChange}
                    />
                </DialogContent>
                <DialogFooter>
                    <DefaultButton onClick={cancelChangeOrg} style={cancelButtonStyle} text="Cancel" />
                    <PrimaryButton disabled={!chosenOption.key} onClick={commitChooseOrg} text="Ok" />
                </DialogFooter>
            </Dialog>
        );
    }

    return { switchOrgDisabled, switchOrg, getOrgSwitcherDialog }
}
